import { applySortPreference } from '@renderer/utils/sort-preferences'
import { isReportToUserStagedAction } from '@renderer/chat/integrations/report-to-user/types'
import { isPreviewlessStreamingChat, type SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import type { ReminderItem } from '../../../hooks/use-reminders'
import { isPinnedReminder } from '../../reminders/utils/pinned'
import { dueStateOf } from '../../reminders/utils/due-date'
import type { StagedAction } from '../../../hooks/staged-actions/types'
import { isPinnedStagedAction } from '../../../hooks/staged-actions/utils'

export type TodoEntry =
  | {
      kind: 'chat'
      id: string
      chat: SidepanelChat
      /** Index in the For You feed, which is where an archived row's linger puts it back. */
      feedPosition: number
      time: number
    }
  | { kind: 'reminder'; id: string; reminder: ReminderItem; time: number }

export interface TodoGroup {
  id: 'pinned' | 'time-sensitive' | 'notifications' | 'todos'
  label: string
  todos: Array<TodoEntry>
}

/**
 * Groups the list: pinned reminders and chats under Pinned, then reminders due today or
 * past due under Due Soon, then reports under Notifications, then the
 * other For You chats and reminders interleaved under To-dos. Due Soon
 * runs by due date, the longest overdue first. Pinned follows the saved order,
 * with new pins first; the remaining groups run newest first. A chat sorts by
 * its last activity, a reminder by when it was written.
 * Every group comes back, empty or not, in display order.
 *
 * A reminder is due by `now`, checked off or not, so one that lingers after
 * completing stays where it was.
 *
 * Previewless streaming chats have nothing to show yet, so they stay out and
 * the section counts them in its "in progress" line.
 *
 * `moreChatsToLoad` is the feed's `hasNextPage`. Any chat still unfetched is
 * older than every fetched one, so a reminder in To-dos older than the oldest
 * fetched chat can't be placed yet. It waits for the next page.
 */
export function groupTodos(
  chats: Array<SidepanelChat>,
  reminders: Array<ReminderItem>,
  moreChatsToLoad: boolean,
  isNotification: (chat: SidepanelChat) => boolean = () => false,
  now: number = Date.now(),
  isPinnedChat: (chat: SidepanelChat) => boolean = () => false,
  pinnedOrder: ReadonlyArray<string> = [],
): Array<TodoGroup> {
  const chatEntries = chats.flatMap(
    (chat, feedPosition): Array<ChatTodoEntry> =>
      isPreviewlessStreamingChat(chat)
        ? []
        : [{ kind: 'chat', id: chat.id, chat, feedPosition, time: Date.parse(chat.updatedAt) }],
  )
  const reminderEntries = reminders.map(
    (reminder): ReminderTodoEntry => ({
      kind: 'reminder',
      id: reminder.id,
      reminder,
      time: reminder.createdAt.getTime(),
    }),
  )

  const pinned = [
    ...chatEntries.filter((entry) => isPinnedChat(entry.chat)),
    ...reminderEntries.filter((entry) => isPinnedReminder(entry.reminder)),
  ]
  const timeSensitive = reminderEntries.filter(
    (entry) => !pinned.includes(entry) && dueStateOf(entry.reminder.scheduledAt, now) != null,
  )
  const notifications = chatEntries.filter((entry) => !pinned.includes(entry) && isNotification(entry.chat))
  const rest = [
    ...chatEntries.filter((entry) => !pinned.includes(entry) && !notifications.includes(entry)),
    ...reminderEntries.filter((entry) => !pinned.includes(entry) && !timeSensitive.includes(entry)),
  ]
  const oldestChatTime = Math.min(...chatEntries.map((entry) => entry.time))
  const placeable =
    moreChatsToLoad && chatEntries.length > 0 ? rest.filter((entry) => entry.time >= oldestChatTime) : rest

  return [
    {
      id: 'pinned',
      label: 'Pinned',
      todos: applySortPreference(pinned.sort(byNewestFirst), pinnedOrder, todoEntryKey),
    },
    { id: 'time-sensitive', label: 'Due Soon', todos: timeSensitive.sort(byDueDate) },
    { id: 'notifications', label: 'Notifications', todos: notifications.sort(byNewestFirst) },
    { id: 'todos', label: 'To-dos', todos: placeable.sort(byNewestFirst) },
  ]
}

type ChatTodoEntry = Extract<TodoEntry, { kind: 'chat' }>
type ReminderTodoEntry = Extract<TodoEntry, { kind: 'reminder' }>

/**
 * A chat whose every staged action is a report_to_user: a message to read, with
 * nothing to send. Judged on all its actions rather than the outstanding ones,
 * so an archived report stays in Notifications while the archive lingers.
 */
export function isNotificationChat(
  chat: SidepanelChat,
  actions: ReadonlyMap<string, { toolSet?: string; toolName?: string }>,
): boolean {
  const chatActions = chat.stagedActionIds.flatMap((id) => {
    const action = actions.get(id)
    return action ? [action] : []
  })
  return chatActions.length > 0 && chatActions.every(isReportToUserStagedAction)
}

/** A chat pins through its staged actions, so any one of them pinned pins it. */
export function isPinnedChat(
  chat: SidepanelChat,
  actions: ReadonlyMap<string, Pick<StagedAction, 'metadata'>>,
): boolean {
  return chat.stagedActionIds.some((id) => {
    const action = actions.get(id)
    return action != null && isPinnedStagedAction(action)
  })
}

/** A row easing in or out by height, while a completed to-do closes up. */
export type TodoRowPresence = 'entering' | 'leaving'

export type TodoRow =
  /** `countIsAtLeast` when the feed may hold more to-dos than `count` takes in. */
  | {
      kind: 'label'
      id: TodoGroup['id']
      label: string
      count: number
      countIsAtLeast: boolean
      // Past its first page, so its heading offers Collapse.
      expanded: boolean
      presence?: TodoRowPresence
    }
  /** The blank to-do atop To-dos, which writes a new reminder. */
  | { kind: 'new-reminder' }
  | { kind: 'in-progress' }
  /** Every notification as one to-do, which opens to show them nested under it. */
  | { kind: 'notifications'; todos: Array<TodoEntry>; open: boolean; presence?: TodoRowPresence }
  /** `nested` is set on a row under the notifications to-do: `last` for the bottom one, where the guide ends. */
  | { kind: 'todo'; todo: TodoEntry; nested?: 'branch' | 'last'; presence?: TodoRowPresence }
  | { kind: 'more'; id: TodoGroup['id']; presence?: TodoRowPresence }

/** How many rows each group shows to start with, and how many more each "View more" adds. */
export const TODO_GROUP_PAGE_SIZE = 5

/** The rows each group shows: `TODO_GROUP_PAGE_SIZE` for a group with no entry. */
export type TodoGroupLimits = ReadonlyMap<TodoGroup['id'], number>

export const todoGroupLimit = (limits: TodoGroupLimits, id: TodoGroup['id']) => limits.get(id) ?? TODO_GROUP_PAGE_SIZE

/**
 * Flattens the groups into the rows the list draws. An empty group drops out,
 * except To-dos, which always leads with the blank to-do for a new reminder.
 *
 * Each group's label counts its to-dos, To-dos' taking in the folded
 * notifications too. Every group stops at its limit with a "View more". To-dos
 * keeps its "View more" while `moreToLoad`, since the next For You page may
 * hold more to-dos.
 *
 * Notifications fold into one row atop To-dos, open while `notificationsOpen`.
 *
 * `leavingIds` are to-dos closing up on their way out. Each stays drawn but
 * stops counting, so the row that takes its place eases in as it closes, and a
 * heading or "View more" with nothing left to head eases out with it.
 */
export function listTodoRows(
  groups: Array<TodoGroup>,
  hasChatsInProgress: boolean,
  {
    limits = new Map(),
    notificationsOpen = false,
    moreToLoad = false,
    leavingIds = new Set(),
  }: {
    limits?: TodoGroupLimits
    notificationsOpen?: boolean
    moreToLoad?: boolean
    leavingIds?: ReadonlySet<string>
  } = {},
): Array<TodoRow> {
  const bundled = groups.find((group) => group.id === 'notifications')?.todos ?? []
  const isLeaving = (todo: TodoEntry) => leavingIds.has(todo.id)
  const bundledStaying = bundled.filter((todo) => !isLeaving(todo))
  const shown = groups.filter(
    (group) => group.id !== 'notifications' && (group.todos.length > 0 || group.id === 'todos'),
  )
  return shown.flatMap((group): Array<TodoRow> => {
    const limit = todoGroupLimit(limits, group.id)
    const hasUnloaded = group.id === 'todos' && moreToLoad
    const hasMore = group.todos.length > limit || hasUnloaded
    const staying = group.todos.filter((todo) => !isLeaving(todo))
    const firstPage = group.todos.slice(0, limit)
    const visibleStaying = new Set(staying.slice(0, limit))
    const bundle: Array<TodoRow> =
      group.id === 'todos' && bundled.length > 0
        ? [
            {
              kind: 'notifications',
              todos: bundled,
              open: notificationsOpen,
              ...(bundledStaying.length === 0 ? { presence: 'leaving' as const } : {}),
            },
            ...(notificationsOpen
              ? bundled.map((todo, index) => ({
                  kind: 'todo' as const,
                  todo,
                  nested: index === bundled.length - 1 ? ('last' as const) : ('branch' as const),
                  ...(isLeaving(todo) ? { presence: 'leaving' as const } : {}),
                }))
              : []),
          ]
        : []
    return [
      {
        kind: 'label' as const,
        id: group.id,
        label: group.label,
        count: staying.length + (group.id === 'todos' ? bundledStaying.length : 0),
        countIsAtLeast: hasUnloaded,
        expanded: limit > TODO_GROUP_PAGE_SIZE,
        ...(staying.length === 0 && group.id !== 'todos' ? { presence: 'leaving' as const } : {}),
      },
      ...(group.id === 'todos' ? [{ kind: 'new-reminder' as const }] : []),
      ...bundle,
      ...(group.id === 'todos' && hasChatsInProgress ? [{ kind: 'in-progress' as const }] : []),
      ...group.todos
        .filter((todo) => isLeaving(todo) || visibleStaying.has(todo))
        .map((todo) => {
          const presence = isLeaving(todo)
            ? ('leaving' as const)
            : firstPage.includes(todo)
              ? undefined
              : ('entering' as const)
          return { kind: 'todo' as const, todo, ...(presence ? { presence } : {}) }
        }),
      ...(hasMore
        ? [
            {
              kind: 'more' as const,
              id: group.id,
              ...(staying.length > limit || hasUnloaded ? {} : { presence: 'leaving' as const }),
            },
          ]
        : []),
    ]
  })
}

export const todoRowKey = (row: TodoRow): string => {
  switch (row.kind) {
    case 'label':
      return `label:${row.id}`
    case 'new-reminder':
      return 'new-reminder'
    case 'in-progress':
      return 'in-progress'
    case 'notifications':
      return 'notifications'
    case 'todo':
      return todoEntryKey(row.todo)
    case 'more':
      return `more:${row.id}`
  }
}

const byNewestFirst = (a: TodoEntry, b: TodoEntry) => b.time - a.time

const byDueDate = (a: ReminderTodoEntry, b: ReminderTodoEntry) =>
  (a.reminder.scheduledAt?.getTime() ?? 0) - (b.reminder.scheduledAt?.getTime() ?? 0)

export const todoEntryKey = (todo: TodoEntry): string => `${todo.kind}:${todo.id}`
